import { useEffect, useRef, useState, type ReactNode } from 'react';
import { TAP_AREA, cn } from './cn';
import { Field, Input, Select } from './Field';
import { InfoTip } from './InfoTip';
import { MapPin } from './MapPin';

/**
 * Address and contact, shown and edited the same way everywhere.
 *
 * Before this the product had `[city, state].filter(Boolean).join(', ')`
 * written out in three files, an address line that was captured at
 * registration and then displayed on no screen at all, and no way anywhere to
 * open a place in a map. Those are not three small omissions; they are the
 * same missing component three times.
 */

export interface AddressLike {
  addressLine1?: string | null;
  addressLine2?: string | null;
  locality?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  postOffice?: string | null;
  placeId?: string | null;
  country?: string | null;
  pincode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  mapsUrl?: string | null;
}

/**
 * A landmark already written as a direction — "Near Bajaj Auto gate 3",
 * "Opposite MIDC Fire Station", "Opp. SBI" — is shown as typed. Prefixing it
 * again printed "Near Near Bajaj Auto gate 3". The API's formatAddress holds
 * the same list; keep the two together.
 */
const SAYS_WHERE = /^(near|nearby|nr|opp|opposite|behind|beside|besides|next\s+to|adjacent\s+to|adj|in\s+front\s+of|above|below|off|inside|outside|b\/h|b\/s)(?![a-z])/i;

export function landmarkLine(landmark: string | null | undefined): string | null {
  const text = (landmark ?? '').trim();
  if (!text) return null;
  return SAYS_WHERE.test(text) ? text : `Near ${text}`;
}

/** The lines an Indian address is actually written on, in that order. */
export function addressLines(a: AddressLike | null | undefined): string[] {
  if (!a) return [];
  const cityLine = [
    a.city,
    a.district && a.district !== a.city ? a.district : null,
    a.state,
  ].filter(Boolean).join(', ');
  return [
    a.addressLine1,
    a.addressLine2,
    [a.locality, a.postOffice && a.postOffice !== a.locality ? `${a.postOffice} P.O.` : null].filter(Boolean).join(', '),
    landmarkLine(a.landmark),
    [cityLine, a.pincode].filter(Boolean).join(' '),
  ].map((l) => (l ?? '').trim()).filter(Boolean);
}

/**
 * A link that opens the place in whatever map app the device has.
 *
 * Built here as a fallback when the server did not send one — no key, no SDK,
 * no embedded frame. A worker opens this on mobile data at a factory gate,
 * and a map widget is a payload; a link is a link.
 */
export function toMapsUrl(a: AddressLike | null | undefined): string | null {
  if (!a) return null;
  if (a.mapsUrl) return a.mapsUrl;
  if (typeof a.latitude === 'number' && typeof a.longitude === 'number') {
    return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
  }
  const written = a.formattedAddress ?? addressLines(a).join(', ');
  return written
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(written)}`
    : null;
}

export function AddressBlock({
  address, empty = 'No address recorded', className, showMapLink = true,
}: {
  address: AddressLike | null | undefined;
  empty?: string;
  className?: string;
  showMapLink?: boolean;
}) {
  const lines = addressLines(address);
  const maps = showMapLink ? toMapsUrl(address) : null;

  if (!lines.length) {
    return <p className={cn('text-small text-neutral-500', className)}>{empty}</p>;
  }

  return (
    <div className={className}>
      <address className="not-italic text-body leading-relaxed text-neutral-800">
        {lines.map((l) => <span key={l} className="block">{l}</span>)}
      </address>
      {maps && (
        <a
          href={maps}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex min-h-6 items-center gap-1 text-caption font-medium text-primary-700 underline-offset-2 hover:underline pointer-coarse:min-h-10 max-md:min-h-10"
        >
          Open in Maps
          <span aria-hidden>↗</span>
        </a>
      )}
    </div>
  );
}

/** A phone number that dials, an email that composes. */
export function ContactLine({ label, value, href, mono }: {
  label: string; value: string | null | undefined; href?: string; mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-4 py-1.5">
      <span className="shrink-0 text-caption text-neutral-500">{label}</span>
      {href
        ? (
          // 24px tall at least (a phone number is tapped more than read), and
          // a long address breaks instead of pushing a narrow column sideways.
          <a
            href={href}
            className={cn('inline-flex min-h-6 min-w-0 items-center justify-end break-all text-right text-body text-primary-700 hover:underline', mono && 'font-mono', TAP_AREA)}
          >
            {value}
          </a>
        )
        : <span className={cn('min-w-0 break-words text-right text-body text-neutral-900', mono && 'font-mono')}>{value}</span>}
    </div>
  );
}

export function ContactBlock({ contact, className, children }: {
  contact: {
    contactName?: string | null;
    contactPhone?: string | null;
    contactAltPhone?: string | null;
    contactEmail?: string | null;
    emergencyPhone?: string | null;
  } | null | undefined;
  className?: string;
  children?: ReactNode;
}) {
  const c = contact ?? {};
  const nothing = !c.contactName && !c.contactPhone && !c.contactAltPhone
    && !c.contactEmail && !c.emergencyPhone;
  if (nothing && !children) {
    return <p className={cn('text-small text-neutral-500', className)}>No contact recorded</p>;
  }
  return (
    <div className={cn('divide-y divide-neutral-100', className)}>
      <ContactLine label="Contact" value={c.contactName} />
      <ContactLine label="Phone" value={c.contactPhone} href={c.contactPhone ? `tel:${c.contactPhone}` : undefined} mono />
      <ContactLine label="Alternate" value={c.contactAltPhone} href={c.contactAltPhone ? `tel:${c.contactAltPhone}` : undefined} mono />
      <ContactLine label="Emergency" value={c.emergencyPhone} href={c.emergencyPhone ? `tel:${c.emergencyPhone}` : undefined} mono />
      <ContactLine label="Email" value={c.contactEmail} href={c.contactEmail ? `mailto:${c.contactEmail}` : undefined} />
      {children}
    </div>
  );
}

export type AddressFormValue = Record<string, string>;

/** What a pincode lookup answers. Shaped by the API's `/geo/pincodes/:pin`. */
export interface PincodeLookupResult {
  pincode: string;
  offices: Array<{ officeName: string; officeType?: string | null; district: string; state: string }>;
  state: string | null;
  district: string | null;
  source?: string;
}

export interface PlaceSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetail {
  placeId: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  address: Partial<Record<'addressLine1' | 'locality' | 'city' | 'district' | 'state' | 'country' | 'pincode', string>>;
}

/** The fields the fieldset knows, in the order they are laid out. */
export const ADDRESS_FIELDS = [
  'country', 'pincode', 'state', 'district', 'city', 'locality', 'postOffice',
  'addressLine1', 'addressLine2', 'landmark', 'latitude', 'longitude', 'placeId',
] as const;

/**
 * The address, entered in parts, with the pincode doing the typing.
 *
 * Parts rather than one box because the parts are what get used: the
 * pincode goes into a courier form, the district is a filter, and only the
 * coordinates can open a map. The order is the order an Indian address is
 * filled in from the number outwards: type six digits and the state, the
 * district and the post office arrive — with a choice when the pincode
 * covers more than one office, because 400001 is Fort AND Mumbai GPO and a
 * form that assumed one would be wrong for half the city.
 *
 * Both lookups are handed in rather than imported, so this package stays
 * free of the API client: the console wraps it once (see `AddressFields` in
 * the SaaS app) and every form uses that.
 */
export function AddressFieldset({
  value, onChange, compact = false, errors = {}, lookupPincode, searchPlaces, resolvePlace,
  required = false, disabled = false, idPrefix, withMap = false, hideCountry = false,
  hideLandmark = false, autoLocate = false, brief = false, pincodeRequired = false,
}: {
  value: AddressFormValue;
  onChange: (next: AddressFormValue) => void;
  /** Drops the second line and the coordinates, for a dialog that is already long. */
  compact?: boolean;
  /** Field-level messages from the server, keyed by field name. */
  errors?: Record<string, string | undefined>;
  /** Given a six-digit pincode, what it covers. Absent = no auto-fill. */
  lookupPincode?: (pincode: string) => Promise<PincodeLookupResult>;
  /** Free-text place search, for the exact point on a map. Absent = no map search. */
  searchPlaces?: (q: string) => Promise<PlaceSuggestion[]>;
  resolvePlace?: (placeId: string) => Promise<PlaceDetail>;
  /** Marks line 1, city, state and pincode as required. */
  required?: boolean;
  /** Marks the pincode alone as required: it fills the rest. */
  pincodeRequired?: boolean;
  disabled?: boolean;
  idPrefix?: string;
  /**
   * Set the coordinates with a pin on a map instead of typing them. If the
   * map cannot load, the two inputs come back.
   */
  withMap?: boolean;
  /** No country picker: the address is in India (the value stays IN). */
  hideCountry?: boolean;
  /** No landmark box: the map pin says where the place is (owner's review, 14 Oct 2026). */
  hideLandmark?: boolean;
  /**
   * Put the map pin on the typed address by itself, once the typing pauses,
   * and move it again if the address changes — until somebody places the pin
   * by hand, after which it is theirs.
   */
  autoLocate?: boolean;
  /**
   * Pincode, city, state and one line — nothing else (owner's review,
   * 14 Oct 2026, for a plant). The pincode still fills district and post
   * office into the value behind the scenes.
   */
  brief?: boolean;
}) {
  const [mapDown, setMapDown] = useState(false);
  const showMap = withMap && !mapDown && !brief;
  const [lookup, setLookup] = useState<{
    pincode: string; state: 'loading' | 'done' | 'none' | 'error'; result?: PincodeLookupResult;
  } | null>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [places, setPlaces] = useState<PlaceSuggestion[] | null>(null);
  const [placeBusy, setPlaceBusy] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);
  const seq = useRef(0);
  // The value as of the latest render, for answers that arrive after typing
  // has moved on: writing back a stale copy would undo what was typed since.
  const latest = useRef(value);
  latest.current = value;
  // Whether the current pin was placed by `autoLocate` (and may be moved by
  // it again) rather than by a person.
  const autoPlaced = useRef(false);
  const locateSeq = useRef(0);
  const [locateNote, setLocateNote] = useState<string | null>(null);

  const set = (k: string) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });
  const patch = (fields: Record<string, string>) => onChange({ ...value, ...fields });

  // The pincode fills what it can and never overwrites what somebody typed
  // over it: a district corrected by hand survives a second lookup. The
  // exception is a CHANGED pincode, where the old district is stale.
  async function onPincode(raw: string, previous: string) {
    const pin = raw.replace(/\D/g, '').slice(0, 6);
    const next: Record<string, string> = { pincode: pin };
    if (pin !== previous && previous.length === 6) {
      next.postOffice = '';
    }
    onChange({ ...value, ...next });
    if (pin.length !== 6 || !lookupPincode) { setLookup(null); return; }

    const mine = ++seq.current;
    setLookup({ pincode: pin, state: 'loading' });
    try {
      const result = await lookupPincode(pin);
      if (mine !== seq.current) return;
      if (!result.offices.length) { setLookup({ pincode: pin, state: 'none', result }); return; }
      setLookup({ pincode: pin, state: 'done', result });
      const fill: Record<string, string> = {};
      const changed = pin !== previous;
      if (result.state && (changed || !value.state)) fill.state = result.state;
      if (result.district && (changed || !value.district)) fill.district = result.district;
      // One office: it is the answer. Several: offer them, fill nothing.
      if (result.offices.length === 1) {
        fill.postOffice = result.offices[0]!.officeName;
        // The city is usually the district for a town and the office's
        // locality for a metro; the district is the safer default and the
        // person corrects it in a keystroke.
        if (changed || !value.city) fill.city = result.district ?? result.offices[0]!.district;
      } else if ((changed || !value.city) && result.district) {
        fill.city = result.district;
      }
      // The latest value, not the one from when the digits were typed: a field
      // edited while the lookup was answering must not be overwritten.
      onChange({ ...latest.current, pincode: pin, ...(changed ? { postOffice: '' } : {}), ...fill });
    } catch {
      if (mine === seq.current) setLookup({ pincode: pin, state: 'error' });
    }
  }

  /** Two words before searching: one word ("Apex") matches half the country. */
  const searchable = (q: string) => q.trim().split(/\s+/).filter(Boolean).length >= 2 && q.trim().length >= 4;

  async function runPlaceSearch(q = placeQuery) {
    if (!searchPlaces || !searchable(q)) return;
    setPlaceBusy(true); setPlaceError(null);
    try { setPlaces(await searchPlaces(q.trim())); }
    catch (e) { setPlaceError(e instanceof Error ? e.message : 'Map search is unavailable.'); setPlaces(null); }
    finally { setPlaceBusy(false); }
  }

  // Searches by itself once the typing pauses — no Search button.
  useEffect(() => {
    if (!searchPlaces || !searchable(placeQuery)) { if (!placeQuery.trim()) setPlaces(null); return; }
    const t = setTimeout(() => { void runPlaceSearch(placeQuery); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeQuery]);

  // The pin from the typed address, tried from the street down to the town.
  useEffect(() => {
    if (!autoLocate || disabled || !searchPlaces || !resolvePlace) return;
    if ((value.latitude || value.longitude) && !autoPlaced.current) return;
    const join = (parts: Array<string | undefined>) => parts.filter((x) => x && x.trim()).join(', ');
    const tries = [
      join([value.addressLine1, value.locality, value.city, value.pincode]),
      join([value.locality, value.city, value.pincode]),
      join([value.city, value.district, value.state, value.pincode]),
    ].filter((q, i, all) => q.length >= 4 && all.indexOf(q) === i);
    if (!tries.length) return;
    const mine = ++locateSeq.current;
    const t = setTimeout(async () => {
      for (const q of tries) {
        try {
          const found = await searchPlaces(q);
          if (mine !== locateSeq.current) return;
          const first = found[0];
          if (!first) continue;
          const d = await resolvePlace(first.placeId);
          if (mine !== locateSeq.current) return;
          const cur = latest.current;
          if ((cur.latitude || cur.longitude) && !autoPlaced.current) return;
          autoPlaced.current = true;
          onChange({ ...cur, latitude: String(d.latitude), longitude: String(d.longitude), placeId: d.placeId });
          setLocateNote('Pin placed from the address. Drag it, or click the map, if it is not exactly right.');
          return;
        } catch { /* the next, shorter wording */ }
      }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocate, value.addressLine1, value.locality, value.city, value.district, value.state, value.pincode]);

  async function choosePlace(p: PlaceSuggestion) {
    if (!resolvePlace) return;
    autoPlaced.current = false;
    setPlaceBusy(true); setPlaceError(null);
    try {
      const d = await resolvePlace(p.placeId);
      const fill: Record<string, string> = {
        placeId: d.placeId,
        latitude: String(d.latitude),
        longitude: String(d.longitude),
      };
      for (const [k, v] of Object.entries(d.address)) if (v) fill[k] = v;
      patch(fill);
      setPlaces(null);
      setPlaceQuery('');
      if (d.address.pincode && d.address.pincode !== value.pincode) void onPincode(d.address.pincode, value.pincode ?? '');
    } catch (e) {
      setPlaceError(e instanceof Error ? e.message : 'Could not read that place.');
    } finally { setPlaceBusy(false); }
  }

  const offices = lookup?.state === 'done' ? lookup.result!.offices : [];
  const multiOffice = offices.length > 1;

  const pincodeHint = lookup?.state === 'loading' ? 'Looking up…'
    // In the brief form the filled city and state already say it.
    : lookup?.state === 'done' && brief ? undefined
    : lookup?.state === 'done' ? `${offices.length} post office${offices.length === 1 ? '' : 's'} · ${lookup.result!.district ?? 'several districts'}, ${lookup.result!.state ?? 'several states'}`
    : lookup?.state === 'none' ? 'Not in the directory — fill the rest by hand'
    : lookup?.state === 'error' ? 'Lookup failed — fill the rest by hand'
    : undefined;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {!hideCountry && !brief && <Field label="Country" error={errors.country} className="sm:col-span-1">
        {(p) => (
          <Select {...p} value={value.country || 'IN'} onChange={set('country')} disabled={disabled}>
            <option value="IN">India</option>
            <option value="NP">Nepal</option>
            <option value="BD">Bangladesh</option>
            <option value="LK">Sri Lanka</option>
            <option value="AE">United Arab Emirates</option>
            <option value="OT">Other</option>
          </Select>
        )}
      </Field>}

      <Field label="Pincode" error={errors.pincode} required={required || pincodeRequired} status={pincodeHint}>
        {(p) => (
          <Input
            {...p}
            value={value.pincode ?? ''}
            onChange={(e) => void onPincode(e.target.value, value.pincode ?? '')}
            inputMode="numeric"
            placeholder="6 digits"
            disabled={disabled}
            autoComplete="postal-code"
            id={idPrefix ? `${idPrefix}-pincode` : p.id}
          />
        )}
      </Field>

      <Field label="State" error={errors.state} required={required}>
        {(p) => <Input {...p} value={value.state ?? ''} onChange={set('state')} disabled={disabled} autoComplete="address-level1" />}
      </Field>

      {!brief && (
        <Field label="District" error={errors.district}>
          {(p) => <Input {...p} value={value.district ?? ''} onChange={set('district')} disabled={disabled} />}
        </Field>
      )}

      <Field label="City / town" error={errors.city} required={required}>
        {(p) => <Input {...p} value={value.city ?? ''} onChange={set('city')} disabled={disabled} autoComplete="address-level2" />}
      </Field>

      {!brief && (
        <Field label="Area / locality" error={errors.locality}>
          {(p) => <Input {...p} value={value.locality ?? ''} onChange={set('locality')} disabled={disabled} />}
        </Field>
      )}

      {!brief && <Field
        label="Post office"
        error={errors.postOffice}
        status={multiOffice ? 'This pincode covers several — choose the right one' : undefined}
        className="sm:col-span-2"
      >
        {(p) => multiOffice ? (
          <Select
            {...p}
            value={value.postOffice ?? ''}
            onChange={(e) => {
              const office = offices.find((o) => o.officeName === e.target.value);
              patch({
                postOffice: e.target.value,
                ...(office && !value.district ? { district: office.district } : {}),
                ...(office && !value.state ? { state: office.state } : {}),
              });
            }}
            disabled={disabled}
          >
            <option value="">Choose the post office…</option>
            {offices.map((o) => (
              <option key={o.officeName} value={o.officeName}>
                {o.officeName}{o.officeType ? ` (${o.officeType})` : ''} — {o.district}
              </option>
            ))}
          </Select>
        ) : (
          <Input {...p} value={value.postOffice ?? ''} onChange={set('postOffice')} disabled={disabled}
            placeholder="Filled from the pincode" />
        )}
      </Field>}

      <Field label={brief ? 'Address' : 'Address line 1'} error={errors.addressLine1} required={required} className={brief ? undefined : 'sm:col-span-2'}>
        {(p) => (
          <Input {...p} value={value.addressLine1 ?? ''} onChange={set('addressLine1')}
            placeholder="Plot / building / street" disabled={disabled} autoComplete="address-line1" />
        )}
      </Field>

      {!compact && !brief && (
        <Field label="Address line 2" error={errors.addressLine2} className="sm:col-span-2">
          {(p) => (
            <Input {...p} value={value.addressLine2 ?? ''} onChange={set('addressLine2')} disabled={disabled} autoComplete="address-line2" />
          )}
        </Field>
      )}

      {!hideLandmark && !brief && (
        <Field label="Landmark" hint="What somebody is told to look for" error={errors.landmark} className="sm:col-span-2">
          {(p) => (
            <Input {...p} value={value.landmark ?? ''} onChange={set('landmark')}
              disabled={disabled} />
          )}
        </Field>
      )}

      {searchPlaces && resolvePlace && !disabled && !brief && (
        <div className="sm:col-span-2 rounded-card border border-dashed border-neutral-300 p-3">
          <Field label="Find on the map" hint="Type the place and town; results appear as you type" status={placeBusy ? 'Searching…' : undefined}>
            {(p) => (
              <Input {...p} value={placeQuery} onChange={(e) => setPlaceQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runPlaceSearch(); } }}
                placeholder="Place name and town" />
            )}
          </Field>
          {placeError && <p className="mt-2 text-caption text-danger-600">{placeError}</p>}
          {places && places.length === 0 && <p className="mt-2 text-caption text-neutral-500">No places match.</p>}
          {places && places.length > 0 && (
            <ul className="mt-2 divide-y divide-neutral-100 rounded border border-neutral-200">
              {places.map((pl) => (
                <li key={pl.placeId}>
                  <button type="button" onClick={() => void choosePlace(pl)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-neutral-50">
                    <span className="text-small text-neutral-900">{pl.mainText}</span>
                    <span className="text-caption font-normal text-neutral-500">{pl.secondaryText}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showMap && (
        <div className="sm:col-span-2 space-y-1.5">
          <p className="flex items-center gap-1 text-small font-medium text-neutral-700">
            Exact location
            <InfoTip label="About the map pin">Search above, or click the map to drop a pin. Drag the pin to adjust it.</InfoTip>
          </p>
          {locateNote && <p className="text-caption text-primary-700">{locateNote}</p>}
          <MapPin
            latitude={value.latitude ? Number(value.latitude) : null}
            longitude={value.longitude ? Number(value.longitude) : null}
            onChange={(lat, lng) => { autoPlaced.current = false; setLocateNote(null); patch({ latitude: String(lat), longitude: String(lng) }); }}
            onUnavailable={() => setMapDown(true)}
            disabled={disabled}
          />
          {value.latitude && value.longitude && (
            <p className="text-caption text-neutral-500">
              Pin at {value.latitude}, {value.longitude}.{' '}
              {!disabled && (
                <button type="button" className="text-primary-700 underline-offset-2 hover:underline"
                  onClick={() => { autoPlaced.current = false; setLocateNote(null); patch({ latitude: '', longitude: '', placeId: '' }); }}>
                  Remove pin
                </button>
              )}
            </p>
          )}
        </div>
      )}

      {!compact && !brief && !showMap && (
        <>
          <Field label="Latitude (optional)" status={value.placeId ? 'Set from the map' : undefined} error={errors.latitude}>
            {(p) => <Input {...p} value={value.latitude ?? ''} onChange={set('latitude')} inputMode="decimal" disabled={disabled} />}
          </Field>
          <Field label="Longitude (optional)" error={errors.longitude}>
            {(p) => <Input {...p} value={value.longitude ?? ''} onChange={set('longitude')} inputMode="decimal" disabled={disabled} />}
          </Field>
        </>
      )}
      {(value.latitude || value.longitude) && (
        <p className="sm:col-span-2 -mt-1 text-caption font-normal text-neutral-500">
          {toMapsUrl({ latitude: Number(value.latitude), longitude: Number(value.longitude) })
            ? <a className="text-primary-700 underline-offset-2 hover:underline" target="_blank" rel="noreferrer"
                href={toMapsUrl({ latitude: Number(value.latitude), longitude: Number(value.longitude) })!}>
                Check this point on a map ↗
              </a>
            : null}
        </p>
      )}
    </div>
  );
}

/**
 * Turns the form's strings into the payload the API takes: blanks are dropped
 * rather than sent as empty strings, and the two coordinates are numbers.
 *
 * `clearable` sends `null` for a blank that used to hold a value, so an
 * edit can remove a landmark — a create simply omits it.
 */
export function addressPayload(v: AddressFormValue, opts: { clearable?: boolean } = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of ['addressLine1', 'addressLine2', 'locality', 'landmark',
    'city', 'district', 'state', 'pincode', 'postOffice', 'placeId', 'country'] as const) {
    const value = v[k]?.trim();
    if (value) out[k] = value;
    else if (opts.clearable && k !== 'country') out[k] = null;
  }
  for (const k of ['latitude', 'longitude'] as const) {
    const n = Number(v[k]);
    if (v[k]?.trim() && Number.isFinite(n)) out[k] = n;
    else if (opts.clearable) out[k] = null;
  }
  return out;
}

/** The reverse: a record from the API into the form's strings. */
export function addressForm(a: AddressLike & { district?: string | null; postOffice?: string | null; placeId?: string | null } | null | undefined): AddressFormValue {
  const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  return {
    country: s(a?.country) || 'IN',
    pincode: s(a?.pincode),
    state: s(a?.state),
    district: s(a?.district),
    city: s(a?.city),
    locality: s(a?.locality),
    postOffice: s(a?.postOffice),
    addressLine1: s(a?.addressLine1),
    addressLine2: s(a?.addressLine2),
    landmark: s(a?.landmark),
    latitude: s(a?.latitude),
    longitude: s(a?.longitude),
    placeId: s(a?.placeId),
  };
}
